import { useAppAuth } from '@/contexts/app-auth.context'
import { Link } from 'react-router-dom'
import {
  BadgeCheck,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Settings,
  Shield,
  Users,
} from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { WorkOSUser } from '@super/shared-types'

export function NavUser({ user }: { user: WorkOSUser }) {
  const { isMobile } = useSidebar()
  const { signOut } = useAppAuth()

  async function handleSignOut() {
    console.log('handleSignOut')

    try {
      // Simply call signOut which redirects to backend logout
      signOut()
    } catch (error) {
      console.error('Error signing out:', error)
      // Fallback: redirect to home page
      window.location.href = '/'
    }
  }

  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email

  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <SidebarMenu>
      <SidebarMenuItem className="bg-sidebar-accent rounded-lg border border-sidebar-border">
        <Popover>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              tooltip={displayName}
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={user.profilePictureUrl || undefined}
                  alt={displayName}
                />
                <AvatarFallback className="rounded-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-0"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
          >
            <div className="flex items-center gap-2 p-3 border-b">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={user.profilePictureUrl || undefined}
                  alt={displayName}
                />
                <AvatarFallback className="rounded-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
            </div>

            <div className="p-1">
              <div className="py-1">
                <Link
                  to="/dashboard/user-profile"
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm"
                >
                  <BadgeCheck className="h-4 w-4" />
                  Profile
                </Link>
                <Link
                  to="/dashboard/user-sessions"
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm"
                >
                  <Settings className="h-4 w-4" />
                  Sessions
                </Link>
                <Link
                  to="/dashboard/user-security"
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm"
                >
                  <Shield className="h-4 w-4" />
                  Security
                </Link>
                <Link
                  to="/dashboard/users"
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm"
                >
                  <Users className="h-4 w-4" />
                  Users
                </Link>
                <Link
                  to="/dashboard/organization-switcher"
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm"
                >
                  <CreditCard className="h-4 w-4" />
                  Organizations
                </Link>
              </div>

              <div className="border-t my-1"></div>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm w-full text-left"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
